
import * as React from "react";
import { FvcSession, MipSession, MepSession, SnipSession, FvcTest, MipTest, MepTest, SnipTest, PefTest, PefSession } from "webportalclient";
import { ArtifactAcceptability, ArtifactAcceptabilityIds } from "../../Shared/PFTAPITypes";
import { DynamicResultsGroupHeading } from "../../Shared/ResultsGroupHeading";
import { useResultsContext } from "../UseResultsContext";

export interface NextGenQualityAcceptabilityProps {
    session: FvcSession;
}

interface QualityRowProps {
    title: string;
    artifact: (s: FvcTest | MipTest | MepTest | SnipTest | PefTest) => ArtifactAcceptabilityIds;
}

export const NextGenQualityAcceptability = ({ session }: NextGenQualityAcceptabilityProps) => {
    const { showReversibilityAbsoluteChange, showReversibilityPercentageChange, showReversibilityBaseValue } = useResultsContext();

    const QualityAcceptability = (artifact) => {
        switch (artifact) {
            case ArtifactAcceptabilityIds.Undetermined:
                return <i aria-hidden="true" title="Undetermined">-</i>
            case ArtifactAcceptabilityIds.Acceptable:
                return <i className="fas fa-check text-success" aria-hidden="true" title="Acceptable"></i>
            case ArtifactAcceptabilityIds.Usable:
                return <i className="far fa-circle text-warning" aria-hidden="true" title="Usable"></i>
            case ArtifactAcceptabilityIds.NotAcceptable:
                return <i className="fas fa-times text-danger" aria-hidden="true" title="Not Acceptable"></i>
        }
    }

    const QualityRow = ({ title, artifact }: QualityRowProps) => {
        return (
            <tr>
                <td key="QualityTitle">
                    {title}
                </td>
                {(session.tests.map((t) => {
                    return (
                        <td key={`Quality${t.id}`}>
                            {artifact(t) !== null && artifact(t) !== undefined ? QualityAcceptability(artifact(t)) : "-"}
                        </td>
                    );
                }))}
                <td key="QualityEmpty"></td>
                {showReversibilityBaseValue ? <td></td> : null}
                {showReversibilityAbsoluteChange ? <td></td> : null}
                {showReversibilityPercentageChange ? <td></td> : null}
            </tr>
        );
    }

    const isSpirometry = (session: FvcSession | MipSession | MepSession | SnipSession): session is FvcSession => {
        return (session as FvcSession).tests[0]?.flowPlateauQuality !== undefined;
    }
    const isPressure = (session: FvcSession | MipSession | MepSession | SnipSession): session is MipSession | MepSession | SnipSession => {
        return (session as MipSession | MepSession | SnipSession).tests[0]?.shortDurationQuality !== undefined;
    }

    const isPef = (session: PefSession): session is PefSession => {
        return (session as PefSession).tests[0]?.coughFreeQuality !== undefined;
    }

    const QualityRows = () => {
        if (isSpirometry(session)) {
            return (
                <>
                    <QualityRow title="Start of Test" artifact={(tr) => (tr as FvcTest).startOfTestQuality as ArtifactAcceptabilityIds} />
                    <QualityRow key="" title="Flow Plateau" artifact={(tr) => (tr as FvcTest).flowPlateauQuality as ArtifactAcceptabilityIds} />
                    <QualityRow title="Cough Free" artifact={(tr) => (tr as FvcTest).coughFreeQuality as ArtifactAcceptabilityIds} />
                    <QualityRow title="Test Duration (ATS 2005 only)" artifact={(tr) => (tr as FvcTest).testDurationQuality as ArtifactAcceptabilityIds} />
                    <QualityRow title="EOFE" artifact={(tr) => (tr as FvcTest).eofeQuality as ArtifactAcceptabilityIds} />
                    <QualityRow title="FIVC - FVC" artifact={(tr) => (tr as FvcTest).fivcFvcQuality as ArtifactAcceptabilityIds} />
                    <QualityRow title="FEV1" artifact={(tr) => (tr as FvcTest).fev1Quality as ArtifactAcceptabilityIds} />
                    <QualityRow title="FVC" artifact={(tr) => (tr as FvcTest).fvcQuality as ArtifactAcceptabilityIds} />
                </>
            )
        } else if (isPressure(session)) {
            return <QualityRow title="Short Duration" artifact={(tr) => (tr as MipTest | MepTest | SnipTest).shortDurationQuality as ArtifactAcceptabilityIds} />;
        } else if (isPef(session)) {
            return <>
                <QualityRow title="Start of Test" artifact={(tr) => (tr as PefTest).startOfTestQuality as ArtifactAcceptabilityIds} />
                <QualityRow title="Cough Free" artifact={(tr) => (tr as PefTest).coughFreeQuality as ArtifactAcceptabilityIds} />
            </>
        }
    }

    return (
        <>
            <DynamicResultsGroupHeading resultsCount={session.tests.length} title="Acceptability" />
            {QualityRows()}
        </>
    );
}
